import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { fmtISO } from '../../lib/shopDetail/helpers';

/**
 * Promotions card — per-shop free-trial editor.
 * Master admins can set a fixed number of trial days or an explicit
 * date range; Stripe honours this at the next subscription checkout.
 * Follows the same .panel / .ph / .infrow visual pattern as its
 * neighbours on this page so the right-rail reads as one consistent
 * stack.
 */
export const PromotionsCard = ({ shopId, subscriptionStatus }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [resolved, setResolved] = useState(null);
  const [mode, setMode] = useState('none');
  const [days, setDays] = useState('14');
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  const call = async (method, body) => {
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/promotions/${shopId}`, {
      method,
      headers: { Authorization: `Bearer ${session?.access_token}`, 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });
    const txt = await res.text();
    let j = {}; try { j = JSON.parse(txt); } catch (_e) { /* raw */ }
    if (!res.ok) throw new Error(j.detail || txt || `HTTP ${res.status}`);
    return j;
  };

  const load = async () => {
    setLoading(true); setError('');
    try {
      const [j, r] = await Promise.all([
        call('GET'),
        (async () => {
          const { data: { session } } = await supabase.auth.getSession();
          const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/promotions/${shopId}/resolved`, {
            headers: { Authorization: `Bearer ${session?.access_token}` },
          });
          return res.ok ? res.json() : null;
        })(),
      ]);
      setData(j);
      setResolved(r);
      setMode(j.trial_mode || 'none');
      if (j.trial_mode === 'days') setDays(String(j.trial_days || 14));
      if (j.trial_mode === 'range') {
        setRangeStart((j.trial_starts_at || '').slice(0, 10));
        setRangeEnd((j.trial_ends_at || '').slice(0, 10));
      }
    } catch (e) {
      setError(e.message || 'Failed to load promotion');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [shopId]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = (() => {
    const currentMode = data?.trial_mode || 'none';
    if (mode !== currentMode) return true;
    if (mode === 'days')  return String(data?.trial_days || '') !== String(days || '');
    if (mode === 'range') {
      return (data?.trial_starts_at || '').slice(0, 10) !== rangeStart ||
             (data?.trial_ends_at   || '').slice(0, 10) !== rangeEnd;
    }
    return false;
  })();

  const save = async () => {
    setSaving(true); setMsg(''); setError('');
    try {
      const payload = mode === 'none'
        ? { trial_mode: null }
        : mode === 'days'
        ? { trial_mode: 'days', trial_days: Number(days) }
        : { trial_mode: 'range', trial_starts_at: rangeStart, trial_ends_at: rangeEnd };
      const j = await call('PUT', payload);
      setData(j);
      setMsg('Saved');
      setTimeout(() => setMsg(''), 2000);
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const isLive = subscriptionStatus === 'active' || subscriptionStatus === 'trialing';

  return (
    <div className="panel" data-testid="promotions-card">
      <div className="ph">
        <h3>Promotions</h3>
        <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-mute)' }}>Free trial</span>
      </div>
      {loading ? (
        <div style={{ padding: 24, color: 'var(--text-mute)', fontSize: 'var(--fs-sm)' }}>Loading…</div>
      ) : (
        <>
          <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--fs-base)' }}>
              <input
                type="radio" name={`promo-mode-${shopId}`}
                checked={mode === 'none'}
                onChange={() => setMode('none')}
                data-testid="promotions-mode-none"
              />
              No promotion
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--fs-base)' }}>
              <input
                type="radio" name={`promo-mode-${shopId}`}
                checked={mode === 'days'}
                onChange={() => setMode('days')}
                data-testid="promotions-mode-days"
              />
              Fixed trial length
            </label>
            {mode === 'days' && (
              <div style={{ marginLeft: 24, display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  className="inp"
                  type="number" min="1" max="365" step="1"
                  value={days}
                  onChange={(e) => setDays(e.target.value)}
                  style={{ width: 90 }}
                  data-testid="promotions-days-input"
                />
                <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-mute)' }}>days from subscription start</span>
              </div>
            )}
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--fs-base)' }}>
              <input
                type="radio" name={`promo-mode-${shopId}`}
                checked={mode === 'range'}
                onChange={() => setMode('range')}
                data-testid="promotions-mode-range"
              />
              Date range
            </label>
            {mode === 'range' && (
              <div style={{ marginLeft: 24, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-mute)', minWidth: 42 }}>Start</span>
                  <input
                    className="inp" type="date"
                    value={rangeStart}
                    onChange={(e) => setRangeStart(e.target.value)}
                    style={{ maxWidth: 180 }}
                    data-testid="promotions-range-start"
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-mute)', minWidth: 42 }}>End</span>
                  <input
                    className="inp" type="date"
                    value={rangeEnd}
                    onChange={(e) => setRangeEnd(e.target.value)}
                    style={{ maxWidth: 180 }}
                    data-testid="promotions-range-end"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="infrow" data-testid="promotions-preview">
            <span className="k">Preview</span>
            <span className="vv" style={{ fontSize: 'var(--fs-sm)' }}>
              {mode === 'none'
                ? <span style={{ color: 'var(--text-mute)' }}>No trial — customer is billed on day one.</span>
                : data?.effective_days
                  ? <>{data.effective_days} free day{data.effective_days === 1 ? '' : 's'} · ends {fmtISO(data.effective_trial_end)}</>
                  : <span style={{ color: 'var(--text-mute)' }}>Unsaved — save to see the preview.</span>}
            </span>
          </div>

          {resolved && resolved.global_signup_days > 0 && (
            <div className="infrow" data-testid="promotions-global-default">
              <span className="k">Global default</span>
              <span className="vv" style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-mute)' }}>
                {resolved.global_signup_days}-day signup trial
                {mode !== 'none'
                  ? ' · overridden by the per-shop promotion above'
                  : resolved.has_subscription
                    ? ' · not applied — this shop already has a Stripe subscription'
                    : ' · applies at first checkout'}
              </span>
            </div>
          )}

          {isLive && dirty && (
            <div style={{ padding: '10px 16px', fontSize: 'var(--fs-xs)', color: 'var(--text-mute)', borderTop: '1px solid var(--line2)' }}>
              This shop already has a live subscription — changes only affect the next Stripe checkout, not the current billing cycle.
            </div>
          )}

          {error && (
            <div className="modal-error" style={{ margin: 12 }} data-testid="promotions-error">{error}</div>
          )}

          <div style={{ padding: '12px 16px', borderTop: '1px solid var(--line2)', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10 }}>
            {msg && <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--green)', fontWeight: 'var(--fw-semibold)' }} data-testid="promotions-msg">{msg}</span>}
            <button
              className="btn p"
              onClick={save}
              disabled={!dirty || saving}
              data-testid="promotions-save"
            >
              {saving ? 'Saving…' : 'Save promotion'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};
